import React, {useEffect, useState, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {shallowEqual} from 'react-redux'
import {useLocation} from 'react-router'
import {
  selectIncentiveCoupon,
  selectMergedCoupons,
  selectUpgradePaymentPreview,
} from '../app-store/coupon'
import {CartDrawer} from './cc-sidebar-components/cart-drawer'
import checkIfIncentiveApplies from '../utils/check-if-incentive-applies'
import {ProductSection} from './cc-sidebar-components/product-section'
import {TransitionOverview} from './cc-sidebar-components/transition-overview'
import {SidebarContainer} from './ui/cc-sidebar'
import {
  BC_PLANS,
  CC_THRYV_SUCCESS_SERVICES_CODES,
  MC_PLANS,
} from '../utils/constants'
import {formatPrice} from '../utils/currency-format'

const APPS_IN_CENTERS = [
  'Thryv Leads',
  'SEO Non-Guaranteed',
  'SEO Money Back Guarantee',
]

const FREE_ACCOUNT_CODES = [
  'appmarket-5-thryv-m2m',
  'cc_basicpls-5-thryv-m2m',
  'cc_basic-5-thryv-m2m',
  'addon-5-signatures_usage',
  'app-websitebuilder',
]

function PriceItem({title, amount, country = 'US'}) {
  return (
    <div className="d-flex justify-content-between">
      <div className="d-flex">
        <p>{title}</p>
      </div>
      <p className="mr-2">{formatPrice(amount, 1, '', country)}</p>
    </div>
  )
}

function CcSidebar({
  selectedCentersAndApps,
  all5PlanPrices,
  country,
  eligibleTransitionCenters,
  defaultCentersAndApps,
  previewRequestFinished,
  setCurrentPrice = () => {},
  setTotalCcPrice = () => {},
  isCohort,
  accountInfo,
  isWebsiteValid,
  isNewTLMCAccessible,
}) {
  const incentiveCoupon = useSelector(selectIncentiveCoupon)
  const orderCouponCodes = useSelector(selectMergedCoupons, shallowEqual)
  const upgradePaymentPreview = useSelector(selectUpgradePaymentPreview)
  const [incentiveDiscount, setIncentiveDiscount] = useState(0)
  const [discount, setDiscount] = useState(0)
  const {pathname} = useLocation()
  const [totalPrice, setTotalPrice] = useState(0)

  const stringifiedEligibleTransitionCenters = JSON.stringify(
    eligibleTransitionCenters,
  )
  const isWebsiteFeeNotIncluded =
    selectedCentersAndApps.apps.some(app => app.id === 'sww4h7k72v8q') &&
    isWebsiteValid

  const filteredFees =
    isWebsiteValid || isWebsiteFeeNotIncluded
      ? selectedCentersAndApps.oneTimes.filter(fee => fee.code !== 'thryvdesg2')
      : selectedCentersAndApps.oneTimes
  const isUpgrade = pathname === '/order/upgrade'

  const isFreeAccount = !(
    defaultCentersAndApps?.centers?.some(
      center => !FREE_ACCOUNT_CODES.includes(center.code),
    ) ||
    defaultCentersAndApps?.apps?.some(
      app => !FREE_ACCOUNT_CODES.includes(app.code),
    )
  )
  const isAus = country === 'AU'
  const isNZ = country === 'NZ'
  const isBCorMCInCart = selectedCentersAndApps.centers.some(
    ({code}) => BC_PLANS.includes(code) || MC_PLANS.includes(code),
  )
  const hasListingsApp = selectedCentersAndApps.apps.some(
    ({code}) => code === 'app-listings',
  )

  const selectedPlanCodes = useMemo(
    () => [
      ...selectedCentersAndApps.centers,
      ...selectedCentersAndApps.apps,
      ...selectedCentersAndApps.oneTimes,
    ],
    [
      selectedCentersAndApps.apps,
      selectedCentersAndApps.centers,
      selectedCentersAndApps.oneTimes,
    ],
  )

  const getTodaySubtotal = isAusNZ => {
    if (!isUpgrade || !upgradePaymentPreview) {
      // add credit as in earlier case we are deducting the credit
      return totalPrice > 0
        ? totalPrice - (accountInfo?.balanceInfo?.availableCreditAmount || 0)
        : '0.00'
    }
    let {subtotal, tax} = upgradePaymentPreview
    // Removing discount for MC Pro Enhanced from subtotal
    if (
      selectedCentersAndApps.centers.some(
        center => center.code === 'mc_pro_enh-5-thryv-m2m',
      )
    ) {
      const discount = upgradePaymentPreview.centers.find(
        center => center.planCode === 'mc_pro_enh-5-thryv-m2m',
      )?.discount
      if (discount) subtotal -= discount
    }
    return !isAusNZ ? subtotal : subtotal + tax
  }

  const renderTaxContent = () => {
    if (isAus) {
      return (
        <div className="mt-2 text-right font-bold">
          <p>*All Plans include GST {!upgradePaymentPreview && 'except SEO'}</p>
        </div>
      )
    }
    if (!isNZ && !upgradePaymentPreview) {
      return (
        <div className="text-right">
          <p>+ Applicable Taxes</p>
        </div>
      )
    }
  }

  const calcItemsTotal = items => {
    return items?.centers
      .concat(items?.apps, items?.oneTimes)
      .filter(plan => {
        if (CC_THRYV_SUCCESS_SERVICES_CODES.includes(plan?.code)) {
          return false
        }
        return true
      })
      .map(item => {
        return {
          ...item,
          amount:
            (item?.code === 'addon-5-leadslift-m2m'
              ? item.unitAmount
              : item?.amount) || 0,
        }
      })
      .reduce((acc, item) => {
        const isInvalidQuantity = APPS_IN_CENTERS.includes(item.name)
        const isLiftItem = item?.code === 'addon-5-leadslift-m2m'
        if (isInvalidQuantity || isLiftItem)
          return acc + parseFloat(item.amount)
        return acc + item.amount * (item.quantity || 1)
      }, 0)
  }

  const filteredApps = selectedCentersAndApps.apps.filter(({code, name}) => {
    const isListingsApp = code === 'app-listings'
    if (isListingsApp) return !isBCorMCInCart
    if (isNewTLMCAccessible) {
      if (
        ['Starter Growth Package', 'Expanded Growth Package'].includes(name)
      ) {
        let isTLLeadExist =
          selectedCentersAndApps.centers.find(item =>
            [
              'tl-mc-5-thryv-m2m',
              'tl-tax-5-thryv-m2m',
              'tl-5-thryv-m2m',
            ].includes(item.code),
          ) ||
          selectedCentersAndApps.apps.find(item =>
            [
              'tl-mc-5-thryv-m2m',
              'tl-tax-5-thryv-m2m',
              'tl-5-thryv-m2m',
            ].includes(item.code),
          )
        if (isTLLeadExist) {
          return false
        }
      }
    }
    if (['seombg-5-thryv-m2m', 'seo-5-thryv-m2m'].includes(code)) {
      let isNewSEOExist =
        selectedCentersAndApps.centers.find(
          ({code}) => code === 'seobase-5-thryv-m2m',
        ) ||
        selectedCentersAndApps.apps.find(
          ({code}) => code === 'seobase-5-thryv-m2m',
        )
      if (isNewSEOExist) {
        return false
      }
      return true
    }
    return true
  })
  const getFilteredSelectedAppList = () => {
    const filteredApps = selectedCentersAndApps.apps.filter(({code, name}) => {
      const isListingsApp = code === 'app-listings'
      if (isListingsApp) return !isBCorMCInCart
      if (isNewTLMCAccessible) {
        if (
          ['Starter Growth Package', 'Expanded Growth Package'].includes(name)
        ) {
          let isTLLeadExist =
            selectedCentersAndApps.centers.find(item =>
              [
                'tl-mc-5-thryv-m2m',
                'tl-tax-5-thryv-m2m',
                'tl-5-thryv-m2m',
              ].includes(item.code),
            ) ||
            selectedCentersAndApps.apps.find(item =>
              [
                'tl-mc-5-thryv-m2m',
                'tl-tax-5-thryv-m2m',
                'tl-5-thryv-m2m',
              ].includes(item.code),
            )
          if (isTLLeadExist) {
            return false
          }
        }
      }
      if (['seombg-5-thryv-m2m', 'seo-5-thryv-m2m'].includes(code)) {
        let isNewSEOExist =
          selectedCentersAndApps.centers.find(
            ({code}) => code === 'seobase-5-thryv-m2m',
          ) ||
          selectedCentersAndApps.apps.find(
            ({code}) => code === 'seobase-5-thryv-m2m',
          )
        if (isNewSEOExist) {
          return false
        }
        return true
      }
      return true
    })

    return filteredApps
  }
  const oldPlanTotalPrice = calcItemsTotal(defaultCentersAndApps)

  useEffect(() => {
    setCurrentPrice(oldPlanTotalPrice)
    let totalPrice =
      calcItemsTotal({
        ...selectedCentersAndApps,
        apps: filteredApps,
        oneTimes: filteredFees,
      }) -
      (discount + incentiveDiscount) +
      (accountInfo?.balanceInfo?.amount || 0)
    if (totalPrice < 0) {
      totalPrice = 0
    }

    setTotalCcPrice(totalPrice)
    setTotalPrice(totalPrice)
  }, [
    oldPlanTotalPrice,
    selectedCentersAndApps.centers,
    defaultCentersAndApps?.centers,
    setCurrentPrice,
    setTotalCcPrice,
    accountInfo?.balanceInfo?.amount,
    discount,
    incentiveDiscount,
    filteredApps,
    selectedCentersAndApps,
    filteredFees,
  ])

  useEffect(() => {
    let incentiveAmount = 0

    selectedCentersAndApps.centers.forEach(currentCenter => {
      const showIncentive = checkIfIncentiveApplies(
        currentCenter,
        isUpgrade,
        eligibleTransitionCenters,
        incentiveCoupon,
      )

      if (showIncentive && incentiveCoupon?.code === 'transition75') {
        incentiveAmount += currentCenter.amount * 0.75
      } else if (showIncentive && incentiveCoupon?.code === 'nci') {
        incentiveAmount += currentCenter.amount
      }
    })

    // Intialize the totalDiscount = 0
    let discountAmount = 0
    let allProducts = [
      ...selectedCentersAndApps.centers,
      ...selectedCentersAndApps.apps,
      ...selectedCentersAndApps.oneTimes,
    ]
    // FULSAS-5958: a fixed-amount coupon (coupon.discountAmount) is a single
    // flat reduction that Recurly redeems once per order — confirmed against
    // a real "waive-franchise-billing" redemption, which shows one $250
    // redemption record regardless of how many selected plans it matches.
    // The loop below previously credited the full discountAmount to every
    // matching product and summed those, so a fixed coupon that matched e.g.
    // both MC Plus and Thryv Leads inflated the shown incentive to a
    // multiple of what Recurly actually applied. Track which fixed coupons
    // have already been counted so each contributes at most once.
    const countedFixedCouponCodes = new Set()
    // Loop through each product
    allProducts.forEach(product => {
      console.log('Processing product ::', product)
      //Loop through all coupons
      let productDiscount = 0
      let productDiscountCouponCode = null
      orderCouponCodes.forEach(coupon => {
        console.log('coupon is ::', coupon)
        // Check if coupon is applicable to the product
        let couponDiscountAmount = 0
        const appliesToPlan =
          coupon?.appliesToItems && coupon?.appliesToItems.length > 0
            ? false
            : coupon.appliesToPlans?.some(
                couponPlan => couponPlan === product.code,
              )
        const appliesToItem = coupon?.appliesToItems?.some(
          couponItem => couponItem === product.code,
        )
        const appliesToAllPlans =
          coupon?.appliesToItems?.length > 0
            ? false
            : !!coupon.appliesToAllPlans
        const appliesToAllItems = !!coupon.appliesToAllItems
        const addonCodesInPlan = (product.addons || []).map(a => a.code)
        const couponItemsMatchAddon = !!coupon.items?.some(item =>
          addonCodesInPlan.includes(item.code),
        )

        // Attach coupon if:
        // - coupon targets this plan explicitly
        // - coupon applies to all plans
        // - coupon applies to all items and this plan has addons
        // - coupon.items explicitly matches any addon on this plan
        if (
          appliesToPlan ||
          appliesToAllPlans ||
          appliesToItem ||
          (appliesToAllItems && addonCodesInPlan.length > 0) ||
          couponItemsMatchAddon
        ) {
          console.log(
            'Coupon is applicable ::' +
              coupon +
              ' to product :: ' +
              product.code,
          )
          if (coupon.discountAmount) {
            couponDiscountAmount = countedFixedCouponCodes.has(coupon.code)
              ? 0
              : coupon.discountAmount
          } else if (coupon.discountPercent) {
            couponDiscountAmount =
              (product.code === 'tl-tax-5-thryv-m2m'
                ? 1
                : product.quantity || 1) *
              product.amount *
              (coupon.discountPercent / 100)
          }
          console.log(
            ' coupon discount on the product :: ',
            couponDiscountAmount,
          )
          if (couponDiscountAmount > productDiscount) {
            productDiscount = couponDiscountAmount
            productDiscountCouponCode = coupon.discountAmount
              ? coupon.code
              : null
          }
        }
      })
      if (productDiscountCouponCode) {
        countedFixedCouponCodes.add(productDiscountCouponCode)
      }
      console.log(' Final discount on the product :: ', productDiscount)
      discountAmount = discountAmount + productDiscount
      console.log('Total discount on products so far :: ', discountAmount)
    })

    // orderCouponCodes.forEach(coupon => {
    //   let couponDiscountAmount = 0
    //   if (coupon.discountAmount) {
    //     couponDiscountAmount += coupon.discountAmount
    //   } else if (
    //     coupon.discountPercent &&
    //     (coupon?.appliesToItems?.length > 0 ||
    //       coupon?.appliesToPlans?.length > 0)
    //   ) {
    //     selectedPlanCodes.forEach(plan => {
    //       if (
    //         coupon.code !== 'transition75' &&
    //         coupon.code !== 'nci' &&
    //         !coupon.appliesToPlan?.includes('mc_pro_enh-5-thryv-m2m') &&
    //         coupon?.appliesToItems?.includes(plan?.code)
    //       ) {
    //         couponDiscountAmount +=
    //           (plan.quantity || 1) *
    //           plan.amount *
    //           (coupon.discountPercent / 100)
    //       }
    //     })
    //     selectedPlanCodes.forEach(plan => {
    //       if (
    //         coupon.code !== 'transition75' &&
    //         coupon.code !== 'nci' &&
    //         !coupon.appliesToPlan?.includes('mc_pro_enh-5-thryv-m2m') &&
    //         coupon?.appliesToPlans?.includes(plan?.code)
    //       ) {
    //         couponDiscountAmount +=
    //           (plan.quantity || 1) *
    //           plan.amount *
    //           (coupon.discountPercent / 100)
    //       }
    //     })
    //   } else if (
    //     coupon.discountPercent &&
    //     coupon.appliesToAllPlans &&
    //     !coupon?.appliesToPlans?.length > 0 &&
    //     !coupon?.appliesToItems?.length > 0
    //   ) {
    //     selectedPlanCodes.forEach(plan => {
    //       if (!plan?.code.includes('setupfee')) {
    //         couponDiscountAmount +=
    //           (plan.quantity || 1) *
    //           plan.amount *
    //           (coupon.discountPercent / 100)
    //       }
    //     })
    //   }

    //   discountAmount += couponDiscountAmount
    // })

    setIncentiveDiscount(incentiveAmount)
    setDiscount(discountAmount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCentersAndApps.centers,
    incentiveCoupon,
    orderCouponCodes,
    selectedPlanCodes,
    incentiveDiscount,
    discount,
    stringifiedEligibleTransitionCenters,
    isUpgrade,
  ])

  const rankItem = item => {
    // if (item.code === 'appmarket-5-thryv-m2m') return 1
    // if (
    //   item?.groupTitle === 'Thryv Add-Ons' ||
    //   item.code === 'seobase-5-thryv-m2m' ||
    //   item.code === 'tl-mc-5-thryv-m2m'
    // )
    //   return 2
    // if (item.code === 'addon-5-keap-addons') return 3
    // if (item?.groupTitle === 'Keap Add-Ons') return 4
    // return 5
    if (
      item.code === 'seobase-5-thryv-m2m' ||
      item.code === 'tl-mc-5-thryv-m2m'
    )
      return 1
    if (item.code === 'appmarket-5-thryv-m2m') return 2
    if (item?.groupTitle === 'Thryv Add-Ons') return 3
    if (
      item.code === 'addon-5-keap-addons' ||
      item.code === 'addon-5-keap-addons-annual' ||
      item.code === 'addon-5-keap-addons-legacy-annual' ||
      item.code === 'addon-5-keap-addons-legacy-m2m'
    )
      return 4
    if (item?.groupTitle === 'Keap Add-Ons') return 5
    return 6
  }

  const itemComparator = (a, b) => {
    const rankA = rankItem(a)
    const rankB = rankItem(b)
    return rankA - rankB
  }

  const addOnsList = [
    ...getFilteredSelectedAppList(),
    ...selectedCentersAndApps.centers.filter(({name}) =>
      APPS_IN_CENTERS.includes(name),
    ),
  ].sort(itemComparator)

  const sortedAddOnsList = addOnsList.sort(itemComparator)
  console.log('sortedAddOnsList :: ', sortedAddOnsList)

  const newProductsSections = [
    {
      title: 'Software',
      products: selectedCentersAndApps.centers.filter(
        currentCenter =>
          !APPS_IN_CENTERS.includes(currentCenter.name) &&
          currentCenter.code !== '247support-m2m',
      ),
      eligibleTransitionCenters,
      isUpgrade,
      incentiveCoupon,
      orderCouponCodes,
    },
    {
      title: 'Add-Ons',
      products: sortedAddOnsList,
      orderCouponCodes,
    },
    {
      title: 'One Time Fees',
      products: filteredFees,
    },
  ]

  const oldProductsSections = isUpgrade && [
    {
      title: 'Centers',
      products: defaultCentersAndApps.centers.filter(
        currentCenter =>
          !APPS_IN_CENTERS.includes(currentCenter.name) &&
          currentCenter.code !== '247support-m2m',
      ),
    },
    {
      title: 'Apps',
      products: [
        ...defaultCentersAndApps.apps,
        ...defaultCentersAndApps.centers.filter(({name}) =>
          APPS_IN_CENTERS.includes(name),
        ),
      ],
    },
  ]

  const hasSupportFee = selectedCentersAndApps.centers.find(
    ({code}) => code === '247support-m2m',
  )
  const supportFeePreview = upgradePaymentPreview?.apps?.find(
    ({planCode}) => planCode === '247support-m2m',
  )
  const todayFinancialDetails = [
    {
      title: 'Support Fee',
      amount: supportFeePreview?.subtotal || hasSupportFee?.amount,
    },
    {
      title: 'Sub Total',
      amount: getTodaySubtotal(isAus || isNZ),
    },
    {
      title: 'Tax',
      amount: upgradePaymentPreview?.tax,
    },
    {
      title: 'Total',
      amount: Math.max(upgradePaymentPreview?.total || 0, 0),
    },
    {
      title: 'Credit',
      amount: accountInfo?.balanceInfo?.availableCreditAmount || 0,
    },
  ]

  return (
    <SidebarContainer>
      {isUpgrade && defaultCentersAndApps && (
        <CartDrawer
          title="Old Plan"
          totalPrice={oldPlanTotalPrice}
          country={country}
        >
          {oldProductsSections.map(section => (
            <ProductSection
              key={section.title + 'old'}
              country={country}
              isCohort={isCohort}
              {...section}
              isOld
            />
          ))}
          <div style={{lineHeight: '15px'}}>
            <PriceItem
              title="Sub Total"
              amount={oldPlanTotalPrice}
              country={country}
            />
          </div>
        </CartDrawer>
      )}
      <CartDrawer
        title="Total Due Today"
        totalPrice={
          !upgradePaymentPreview
            ? totalPrice
            : upgradePaymentPreview.total +
                (accountInfo?.balanceInfo?.availableCreditAmount || 0) >
              0
            ? upgradePaymentPreview.total +
              (accountInfo?.balanceInfo?.availableCreditAmount || 0)
            : 0
        }
        defaultOpen
        loading={!upgradePaymentPreview && previewRequestFinished === false}
        country={country}
      >
        {newProductsSections.map(section => (
          <ProductSection
            all5PlanPrices={all5PlanPrices}
            key={section.title}
            upgradePaymentPreview={upgradePaymentPreview}
            country={country}
            {...section}
            selectedCentersAndApps={selectedCentersAndApps}
            isCohort={isCohort}
            orderCouponCodes={orderCouponCodes}
          />
        ))}
        <div style={{lineHeight: '15px'}} className="mt-3">
          {orderCouponCodes &&
            orderCouponCodes.map(coupon => {
              if (
                coupon.code === 'transition75' ||
                coupon.code === 'nci' ||
                coupon.appliesToPlans?.includes('mc_pro_enh-5-thryv-m2m')
              ) {
                return <></>
              }
              let couponDiscountAmount = 0
              if (coupon.discountAmount) {
                couponDiscountAmount += coupon.discountAmount
              } else if (coupon.discountPercent && coupon.appliesToAllPlans) {
                selectedPlanCodes.forEach(plan => {
                  if (!plan?.code.includes('setupfee')) {
                    couponDiscountAmount +=
                      ((plan.quantity || 1) *
                        plan.amount *
                        coupon.discountPercent) /
                      100
                  }
                })
              } else {
                selectedPlanCodes.forEach(plan => {
                  if (
                    (coupon?.appliesToPlans?.includes(plan?.code) &&
                      !plan?.code.includes('setupfee')) ||
                    coupon?.appliesToItems?.includes(plan?.code)
                  ) {
                    couponDiscountAmount +=
                      ((plan.quantity || 1) *
                        plan.amount *
                        coupon.discountPercent) /
                      100
                  }
                })
              }
              if (couponDiscountAmount > 0) {
                return (
                  <div
                    key={coupon.code}
                    className="d-flex justify-content-between"
                  >
                    <div className="d-flex">
                      <p>{coupon.description}</p>
                    </div>
                  </div>
                )
              }
              return <></>
            })}
          {todayFinancialDetails
            .filter(({title}) => {
              if (title === 'Support Fee') {
                return (
                  supportFeePreview || (hasSupportFee && !upgradePaymentPreview)
                )
              }
              if (!upgradePaymentPreview)
                return !['Tax', 'Total'].includes(title)
              if (isAus || isNZ) return title !== 'Tax'

              return true
            })
            .map(({title, amount}) => (
              <PriceItem
                key={title}
                title={title}
                amount={amount}
                country={country}
              />
            ))}
          {renderTaxContent()}
        </div>
      </CartDrawer>
      {!isFreeAccount && (
        <p className="m-3 ml-4">
          * The amount due today is an estimate and actual charge will be a
          prorated amount depending on when you upgrade and what you select.
        </p>
      )}
      {hasListingsApp && isBCorMCInCart && (
        <p className="m-3 ml-4">
          * The remaining prorated balance for the{' '}
          <span className="font-bold">Listings Management</span> will be applied
          to the account.
        </p>
      )}
      {incentiveCoupon?.code === 'transition75' && (
        <TransitionOverview
          selectedCentersAndApps={selectedCentersAndApps}
          eligibleTransitionCenters={eligibleTransitionCenters}
          isUpgrade={isUpgrade}
          country={country}
          upgradePaymentPreview={upgradePaymentPreview}
          accountInfo={accountInfo}
        />
      )}
    </SidebarContainer>
  )
}

export {CcSidebar}
